import { Component, Element, Host, h, Prop, Watch } from "@stencil/core";

/**
 * @slot image - Slot for the base image (`<picture>` or `<img>`)
 * @slot zoom-image - Slot for the zoomed image (`<picture>` or `<img>`)
 */
@Component({
    tag: "ux-iws-nb-image-zoom",
    styleUrl: "nb-image-zoom.scss",
    shadow: false,
})
export class NbImageZoom {
    private baseClass = "ux-iws-nb-image-zoom";
    private zoomImage: HTMLImageElement;
    private containerElement: HTMLDivElement;

    /** The current offset of the zoom image */
    private currentOffset: { x: number; y: number } = { x: 0, y: 0 };
    /** The target offset of the zoom image */
    private targetOffset: { x: number; y: number } = { x: 0, y: 0 };
    /** Controls the smoothness of the zoom effect */
    private offsetTension = 0.25;

    /**
     * The element reference for the component.
     * Used to access the DOM and manipulate styles or classes.
     */
    @Element() element: HTMLUxIwsNbImageZoomElement;

    /**
     * The label for the zoom in button.
     * @default "Zoom in"
     */
    @Prop() zoomInLabel = "Zoom in";

    /**
     * The label for the zoom out button.
     * @default "Zoom out"
     */
    @Prop() zoomOutLabel = "Zoom out";

    /** Indicates if the image is currently displayed zoomed */
    @Prop({ mutable: true, reflect: true }) isZoomed: boolean = false;

    private loadImage = async (image: HTMLImageElement): Promise<void> => {
        if (!image.src && image.getAttribute("data-src")) {
            image.src = image.getAttribute("data-src");
        }
        if (image.complete) {
            return Promise.resolve();
        } else {
            return new Promise((resolve) => {
                image.addEventListener("load", () => resolve(), { once: true });
                image.addEventListener("error", () => resolve(), {
                    once: true,
                });
            });
        }
    };

    private handlePointer = (event: MouseEvent | TouchEvent) => {
        if (
            !this.zoomImage ||
            !this.zoomImage.offsetWidth ||
            !this.zoomImage.offsetHeight
        ) {
            return;
        }
        const pageX =
            event instanceof MouseEvent ? event.pageX : event.touches[0].pageX;
        const pageY =
            event instanceof MouseEvent ? event.pageY : event.touches[0].pageY;
        if (event.cancelable) {
            event.preventDefault();
        }
        const rect = this.containerElement.getBoundingClientRect();
        const relX = (pageX - rect.left) / rect.width;
        const relY = (pageY - rect.top) / rect.height;
        if (relY < 0 || relY > 1 || relX < 0 || relX > 1) {
            return;
        }
        const offsetXMax = Math.max(0, this.zoomImage.offsetWidth - rect.width);
        const offsetYMax = Math.max(
            0,
            this.zoomImage.offsetHeight - rect.height
        );
        const offsetX = -relX * offsetXMax;
        const offsetY = -relY * offsetYMax;
        this.zoomImage.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
        this.targetOffset = { x: offsetX, y: offsetY };
    };

    private centerZoomImage = () => {
        if (
            !this.zoomImage ||
            !this.zoomImage.offsetWidth ||
            !this.zoomImage.offsetHeight
        ) {
            return;
        }
        const rect = this.containerElement.getBoundingClientRect();
        const offsetX = (rect.width - this.zoomImage.offsetWidth) / 2;
        const offsetY = (rect.height - this.zoomImage.offsetHeight) / 2;
        this.currentOffset = { x: offsetX, y: offsetY };
        this.targetOffset = { x: offsetX, y: offsetY };
        this.zoomImage.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
    };

    private updateOffsetLoop = () => {
        if (!this.isZoomed || !this.zoomImage) {
            return;
        }
        const {
            currentOffset: offset,
            targetOffset: targetOffset,
            offsetTension: tension,
        } = this;
        this.currentOffset = {
            x: offset.x + tension * (targetOffset.x - offset.x),
            y: offset.y + 0.5 * (targetOffset.y - offset.y),
        };
        this.zoomImage.style.transform = `translate(${this.currentOffset.x}px, ${this.currentOffset.y}px)`;
        window.requestAnimationFrame(this.updateOffsetLoop);
    };

    private preventScroll = (event: TouchEvent) => {
        if (event.cancelable) {
            event.preventDefault();
        }
    };

    private startZooming = () => {
        if (!this.zoomImage || !this.containerElement) {
            return;
        }
        window.addEventListener("resize", this.stopZooming);
        window.addEventListener("mousemove", this.handlePointer);
        window.requestAnimationFrame(this.updateOffsetLoop);

        this.containerElement.addEventListener(
            "pointerenter",
            this.handlePointer
        );
        this.containerElement.addEventListener(
            "touchstart",
            this.preventScroll,
            {
                passive: false,
            }
        );
        this.containerElement.addEventListener(
            "touchmove",
            this.handlePointer,
            {
                passive: false,
            }
        );
    };

    private stopZooming = () => {
        window.removeEventListener("resize", this.stopZooming);
        window.removeEventListener("mousemove", this.handlePointer);
        if (this.containerElement) {
            this.containerElement.removeEventListener(
                "pointerenter",
                this.handlePointer
            );
            this.containerElement.removeEventListener(
                "touchstart",
                this.preventScroll
            );
            this.containerElement.removeEventListener(
                "touchmove",
                this.handlePointer
            );
        }
        this.isZoomed = false;
    };

    @Watch("isZoomed")
    handleZoomChange() {
        if (!this.isZoomed) {
            this.stopZooming();
            return;
        }
        if (!this.containerElement) {
            return;
        }
        const zoomImage: HTMLImageElement = this.containerElement.querySelector(
            `[slot="zoom-image"] img`
        );
        if (!zoomImage) {
            this.isZoomed = false;
            return;
        }
        this.loadImage(zoomImage).then(() => {
            if (!zoomImage.naturalWidth || !zoomImage.naturalHeight) {
                this.isZoomed = false;
                return;
            }
            this.zoomImage = zoomImage;
            window.requestAnimationFrame(() => {
                this.centerZoomImage();
                this.startZooming();
            });
        });
    }

    componentWillLoad() {
        this.isZoomed = false;
    }

    disconnectedCallback() {
        if (this.isZoomed) {
            this.stopZooming();
            this.isZoomed = false;
        }
    }

    private handlePressZoomButton = (event: MouseEvent | TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.isZoomed = !this.isZoomed;
    };

    render() {
        return (
            <Host class={this.baseClass} role="region" aria-live="polite">
                <div
                    class={`${this.baseClass}__container`}
                    ref={(el) => (this.containerElement = el)}
                >
                    <div
                        class={`${this.baseClass}__image`}
                        aria-hidden={`${!!this.isZoomed}`}
                    >
                        <slot name="image" />
                    </div>
                    <div
                        class={`${this.baseClass}__zoom-image`}
                        aria-hidden={`${!this.isZoomed}`}
                    >
                        <slot name="zoom-image" />
                    </div>
                    <button
                        class={`${this.baseClass}__zoom-button`}
                        type="button"
                        onMouseDown={this.handlePressZoomButton}
                        onTouchStart={this.handlePressZoomButton}
                        aria-label={
                            this.isZoomed ? this.zoomOutLabel : this.zoomInLabel
                        }
                    >
                        <ux-icon
                            rel="zoom-out"
                            name="chevron-inwards"
                            weight="bold"
                            aria-label={this.zoomOutLabel}
                            aria-hidden={`${!!this.isZoomed}`}
                        ></ux-icon>
                        <ux-icon
                            rel="zoom-in"
                            name="chevron-outwards"
                            weight="bold"
                            aria-label={this.zoomInLabel}
                            aria-hidden={`${!this.isZoomed}`}
                        ></ux-icon>
                    </button>
                </div>
            </Host>
        );
    }
}
